import { useState } from "react";
import { IonContent, IonPage } from "@ionic/react";

import { ActionBar } from "../components/ActionBar";
import { Hero } from "../components/Hero";
import { Notice } from "../components/Notice";
import { ProgressBar } from "../components/ProgressBar";
import { DiscloseRow, NavRow, RowGroup } from "../components/Rows";
import { Sheet } from "../components/Sheet";
import { Screen } from "../components/Shell";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { I } from "../components/icons";
import { BADGES } from "../data/badges";
import { cycleOn } from "../domain/cycle";
import { dayLabel, todayStr } from "../domain/date";
import { LEVELS, level, xpBreakdown } from "../domain/progress";
import { CYCLE_BADGES, hoursMinutes } from "../domain/progressView";
import { badgeCtx, nextBadge, stats } from "../domain/stats";
import { lifetime } from "../domain/training";
import type { Badge } from "../domain/types";
import { useKaya } from "../store/kayaStore";
import "./Progress.css";

/** Medals shown in each group before "All N …". */
const FIRST_MEDALS = 6;

function plural(n: number, one: string, many = one + "s"): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

function frac(a: number): string {
  return String(Math.round(a * 10) / 10);
}

function Medal({ b, locked, big }: { b: Badge; locked?: boolean; big?: boolean }) {
  // The emoji is generated data (invariant 2) and lives inside the medal and nowhere else.
  const tone = locked ? " pg-disc--locked" : b.c ? ` pg-disc--${b.c}` : "";
  return (
    <span className={`pg-disc${tone}${big ? " pg-disc--big" : ""}`} aria-hidden="true">
      {b.e}
    </span>
  );
}

/**
 * /progress/level — the level and what moved it (it was a bar with no way to find out), the
 * next badge, every badge earned and still to earn, the XP tally, the ladder and the totals.
 * Replaces /levels and the badge grid that used to close Progress.
 */
export default function ProgressLevel() {
  const S = useKaya((s) => s.S);
  const [open, setOpen] = useState<Badge | null>(null);
  const [allEarned, setAllEarned] = useState(false);
  const [allLocked, setAllLocked] = useState(false);

  const today = todayStr();
  const lv = level(S);
  const next = LEVELS[lv.n] as [number, string] | undefined;
  const toNext = lv.to === null ? null : lv.to - lv.xp;
  const tally = xpBreakdown(S);
  const st = stats(S);
  const ctx = badgeCtx(S);
  const nb = nextBadge(S);
  const cyc = cycleOn(S);
  const life = lifetime(S, today);

  const visible = BADGES.filter((b) => cyc || !CYCLE_BADGES.includes(b.id) || S.badges[b.id]);
  const earned = visible.filter((b) => S.badges[b.id]);
  const locked = visible
    .filter((b) => !S.badges[b.id])
    .map((b) => {
      const [a, n] = b.prog(st, ctx);
      return { b, a, n, r: n ? a / n : 0 };
    })
    .sort((x, y) => y.r - x.r);
  const total = BADGES.filter((b) => cyc || !CYCLE_BADGES.includes(b.id)).length;
  const earnedShown = allEarned ? earned : earned.slice(0, FIRST_MEDALS);
  const lockedShown = allLocked ? locked : locked.slice(0, FIRST_MEDALS);

  const openInfo = open ? S.badges[open.id] : undefined;
  const openProg = open && !openInfo ? open.prog(st, ctx) : null;

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar title="Level & badges" back={{ to: "/progress", label: "Back to Progress" }} />
          <div className="pg">
            <Hero
              label="Level"
              figure={lv.n}
              unit={lv.name}
              line={
                toNext === null || !next
                  ? "Top level. The streak and the badges carry on regardless."
                  : `${toNext.toLocaleString()} XP to Level ${lv.n + 1} · ${next[1]}`
              }
              visual={
                <ProgressBar
                  value={lv.to === null ? 1 : lv.xp - lv.from}
                  max={lv.to === null ? 1 : lv.to - lv.from}
                  label={`${lv.xp.toLocaleString()} XP`}
                  detail={next ? `Level ${lv.n + 1} at ${next[0].toLocaleString()}` : "top level"}
                  ariaLabel={
                    next
                      ? `${lv.xp.toLocaleString()} XP of the ${next[0].toLocaleString()} Level ${lv.n + 1} needs`
                      : `${lv.xp.toLocaleString()} XP, top level`
                  }
                />
              }
            />

            {nb ? (
              <Notice icon={I.medal}>
                Next badge: {nb.b.n}. {nb.b.d} {frac(nb.a)} of {nb.n} so far.
              </Notice>
            ) : null}

            <RowGroup heading={`Earned · ${earned.length} of ${total}`}>
              {earned.length ? (
                <div className="pg-medals">
                  {earnedShown.map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      className="pg-medal"
                      aria-label={`${b.n}, earned${String(S.badges[b.id]) === today ? " today" : ""}`}
                      onClick={() => setOpen(b)}
                    >
                      <Medal b={b} />
                      <span className="pg-medal__nm">{b.n}</span>
                      {String(S.badges[b.id]) === today ? <Tag tone="logged">new</Tag> : null}
                    </button>
                  ))}
                </div>
              ) : (
                <NavRow kind="value" title="None yet" subtitle="Finishing your first session earns the first one." />
              )}
              {earned.length > FIRST_MEDALS ? (
                <NavRow
                  title={allEarned ? "Show fewer" : `All ${earned.length} earned badges`}
                  onClick={() => setAllEarned(!allEarned)}
                />
              ) : null}
            </RowGroup>

            {locked.length ? (
              <RowGroup heading="Not yet">
                <div className="pg-medals">
                  {lockedShown.map(({ b, a, n }) => (
                    <button
                      key={b.id}
                      type="button"
                      className="pg-medal pg-medal--locked"
                      aria-label={`${b.n}, not earned yet, ${frac(a)} of ${n}`}
                      onClick={() => setOpen(b)}
                    >
                      <Medal b={b} locked />
                      <span className="pg-medal__nm">{b.n}</span>
                      <span className="pg-medal__prog">
                        {frac(a)} of {n}
                      </span>
                    </button>
                  ))}
                </div>
                {locked.length > FIRST_MEDALS ? (
                  <NavRow
                    title={allLocked ? "Show fewer" : `All ${locked.length} still to earn`}
                    onClick={() => setAllLocked(!allLocked)}
                  />
                ) : null}
              </RowGroup>
            ) : null}

            <RowGroup heading="XP">
              <DiscloseRow title="Where it came from" value={`${lv.xp.toLocaleString()} XP`}>
                <table className="pg-tally">
                  <tbody>
                    {tally.map((r) => (
                      <tr key={r.id}>
                        <th scope="row">{r.label}</th>
                        <td className="pg-muted">
                          {r.count ? `${r.count.toLocaleString()} × ${r.each}` : `${r.each} each`}
                        </td>
                        <td>{r.total.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th scope="row">Total</th>
                      <td />
                      <td>{lv.xp.toLocaleString()} XP</td>
                    </tr>
                  </tfoot>
                </table>
              </DiscloseRow>
              <NavRow
                kind="value"
                icon={I.clock}
                title="All time"
                subtitle={
                  <>
                    {plural(life.sessions, "session")} · {plural(life.treadmill, "treadmill run")} ·{" "}
                    {plural(life.walks, "walk")} · {hoursMinutes(life.minutes)} active · {life.km.toLocaleString()} km · ~
                    {(Math.round(life.work / 100) * 100).toLocaleString()} Work kcal <Tag tone="est" />
                  </>
                }
              />
            </RowGroup>

            <RowGroup heading="The ladder">
              {LEVELS.map(([need, name], i) => {
                const n = i + 1;
                const now = n === lv.n;
                const todo = n > lv.n;
                return (
                  <NavRow
                    key={name}
                    kind="value"
                    icon={now ? I.progress : todo ? I.lock : I.check}
                    tone={now ? "brand" : "default"}
                    title={`Level ${n} · ${name}`}
                    subtitle={
                      now
                        ? `you are here · ${lv.xp.toLocaleString()} XP`
                        : todo
                          ? `${(need - lv.xp).toLocaleString()} to go`
                          : undefined
                    }
                    value={`${need.toLocaleString()} XP`}
                  />
                );
              })}
            </RowGroup>

            <p className="pg-note">
              XP is a count of what you have done, not a score out of anything. It never goes down.
            </p>

            <RowGroup heading="Connected">
              <NavRow title="Training" icon={I.dumbbell} subtitle="Where the hours went" to="/progress/training" />
            </RowGroup>
          </div>
        </Screen>

        {open ? (
          <Sheet
            title={open.n}
            sub={
              openInfo
                ? typeof openInfo === "string" && /^\d{4}-\d{2}-\d{2}$/.test(openInfo)
                  ? `Earned ${dayLabel(openInfo)}`
                  : "Earned"
                : openProg
                  ? `Not earned yet · ${frac(openProg[0])} of ${openProg[1]} so far`
                  : "Not earned yet"
            }
            onClose={() => setOpen(null)}
            footer={<ActionBar inSheet primary={{ label: "Close", onClick: () => setOpen(null) }} />}
          >
            <Medal b={open} locked={!openInfo} big />
            <p className="pg-note">{open.d}</p>
          </Sheet>
        ) : null}
      </IonContent>
    </IonPage>
  );
}
